import React, { useEffect, useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { fireAlertToast } from '@/design-system/alerts'
import { SettingsNav, SettingsTab } from '../../components/SettingsNav'
import { ProfileSection } from '../../components/ProfileSection'
import { ContentSection } from '../../components/ContentSection'
import { BillingSection } from '../../components/BillingSection'
import { AccountSecuritySection } from '../../components/AccountSecuritySection'
import { DangerZoneSection } from '../../components/DangerZoneSection'
import { PrivacySection } from '../../components/PrivacySection'

const Page = styled(Box)({ maxWidth: 900 })

const H1 = styled(Typography)({ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13.5, color: 'var(--ds-ink-subtle)', marginTop: 6 })

const Layout = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '208px 1fr',
  gap: 36,
  alignItems: 'start',
  marginTop: 28,
  '@media (max-width: 720px)': { gridTemplateColumns: '1fr' },
})

/**
 * `/settings` — replaces the legacy `/profile/me` + orphaned `/profile/v2`
 * pages with one DS-native surface.
 */
export function SettingsScreen() {
  const [tab, setTab] = useState<SettingsTab>('profile')
  const [searchParams, setSearchParams] = useSearchParams()

  // Stripe checkout redirects back here — ported from the legacy MyProfile page.
  useEffect(() => {
    const checkout = searchParams.get('checkout')
    if (checkout === 'success') {
      fireAlertToast('settings.subscribed')
      setTab('billing')
      setSearchParams({}, { replace: true })
    } else if (checkout === 'cancel') {
      fireAlertToast('settings.checkoutCancelled')
      setSearchParams({}, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Page>
      <Box>
        <H1>Settings</H1>
        <Sub>Manage your profile, plan, and how people reach you.</Sub>
      </Box>

      <Layout>
        <SettingsNav value={tab} onChange={setTab} />
        <Box>
          {tab === 'profile' && <ProfileSection />}
          {tab === 'content' && <ContentSection />}
          {tab === 'billing' && <BillingSection />}
          {tab === 'account' && <AccountSecuritySection />}
          {tab === 'privacy' && <PrivacySection />}
          {tab === 'danger' && <DangerZoneSection />}
        </Box>
      </Layout>
    </Page>
  )
}
